import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { from, lastValueFrom, Observable, of, Subject } from "rxjs";
import { finalize, map, shareReplay, tap } from "rxjs/operators";
import AppConstants from "../constants/app.constants";
import { environment } from "src/environments/environment";
import { HttpService } from "./http.service";
import { StorageService } from "./storage.service";
import { TimezoneService } from "./timezone.service";

interface ChatTokenData {
  token: string;
  expireTime: string;
}

export interface ChatTokens {
  acstoken: ChatTokenData;
  botToken: ChatTokenData;
}

interface GenerateChatTokensResponse {
  code: number;
  status: boolean;
  data: ChatTokens;
}

export interface QueuedChatAttachment {
  id: string;
  name: string;
  mimeType: string;
  rawBase64Data: string;
}

export interface QueuedChatMessage {
  localId: string;
  alarmId: string | number;
  type: number;
  message: string;
  thread: string;
  pushMessage: string;
  queuedAt: string;
  attachments: QueuedChatAttachment[];
}

export interface ChatQueueSyncResult {
  localId: string;
  status: "sent" | "failed";
  serverMessageId?: string;
  content?: string;
}

@Injectable({
  providedIn: "root",
})
export class ChatService {
  private chatTokens: ChatTokens | null = null;
  private generateInFlight$: Observable<ChatTokens> | null = null;
  private isSyncingQueue = false;

  // Any open ChatPage can subscribe to reflect sync results (sent/failed) on its own pending bubbles,
  // even though the sync itself now runs globally from app.component and does not depend on a page being open.
  private readonly messageSyncedSource = new Subject<ChatQueueSyncResult>();
  readonly messageSynced$ = this.messageSyncedSource.asObservable();

  constructor(
    private httpService: HttpService,
    private storageService: StorageService,
    private http: HttpClient,
    private timezoneService: TimezoneService
  ) {
    this.chatTokens = this.readTokensFromStorage();
  }

  // Offline message queue: persisted so pending messages survive app restarts and sync once back online.
  async getQueuedMessages(): Promise<QueuedChatMessage[]> {
    const stored = await this.storageService.getItemIonic(AppConstants.CHAT_OFFLINE_MESSAGE_QUEUE);
    return Array.isArray(stored) ? stored : [];
  }

  async queueOfflineMessage(message: QueuedChatMessage): Promise<void> {
    const queue = await this.getQueuedMessages();
    queue.push(message);
    await this.storageService.setItemIonic(AppConstants.CHAT_OFFLINE_MESSAGE_QUEUE, queue);
  }

  // Sends every queued message (any thread/alarm) once connectivity returns; called from app.component so it
  // runs regardless of which page is currently open. Requires the user to be logged in (auth token present).
  async syncQueuedMessages(): Promise<void> {
    if (this.isSyncingQueue) {
      return;
    }

    // Acquire the sync lock immediately to avoid a race where two callers both pass the
    // initial check and proceed concurrently (causing duplicate uploads).
    this.isSyncingQueue = true;

    try {
      const authToken = await this.storageService.getItemIonic(AppConstants.AUTH_TOKEN);
      if (!authToken) {
        return;
      }

      const queue = await this.getQueuedMessages();
      if (!queue.length) {
        return;
      }

      // Debug trace: start of global sync
      try {
        // debug logging removed
      } catch {}

      for (const queuedMessage of queue) {
        try {
          // Debug trace per queued message
          try {
            // debug logging removed
          } catch {}

          let combinedMessage = queuedMessage.message;

          if (queuedMessage.attachments?.length) {
            const fileMarkers: string[] = [];
            for (const attachment of queuedMessage.attachments) {
              fileMarkers.push(await this.uploadQueuedAttachment(attachment));
            }
            combinedMessage = `${fileMarkers.join("")}${queuedMessage.message ? `\n${queuedMessage.message}` : ""}`;
          }

          const chatHeaders = await lastValueFrom(this.getValidChatTokenHeaders());
          const response = await this.httpService.post(
            "chat/create/chat/message",
            {
              alarm_id: queuedMessage.alarmId,
              type: queuedMessage.type,
              message: combinedMessage,
              thread: queuedMessage.thread,
              push_message: queuedMessage.pushMessage,
            },
            chatHeaders
          );

          await this.removeQueuedMessage(queuedMessage.localId);
          this.messageSyncedSource.next({
            localId: queuedMessage.localId,
            status: "sent",
            serverMessageId: response?.data?.messageId,
            content: combinedMessage,
          });
        } catch (error) {
          console.error("Failed to sync offline chat message:", error);
          await this.removeQueuedMessage(queuedMessage.localId);
          this.messageSyncedSource.next({ localId: queuedMessage.localId, status: "failed" });
        }
      }
    } finally {
      this.isSyncingQueue = false;
    }
  }

  private async uploadQueuedAttachment(attachment: QueuedChatAttachment): Promise<string> {
    const fileBlob = this.base64ToBlob(attachment.rawBase64Data, attachment.mimeType);
    const formData = new FormData();
    formData.append("upload_file", fileBlob, attachment.name);

    // debug logging removed

    const headers = await this.buildFileUploadHeaders();
    const responseBody = await lastValueFrom(
      this.http.post(`${environment.apiUrl}chat/chat/file/upload`, formData, { headers })
    );

    const parsed = this.parseAttachmentUploadResponse(responseBody, attachment.name);
    if (!parsed.url) {
      throw new Error(parsed.message || "Attachment upload failed");
    }

    return `${parsed.url}-__FILE__${parsed.fileName}`;
  }

  private async buildFileUploadHeaders(): Promise<HttpHeaders> {
    const chatHeaders = await lastValueFrom(this.getValidChatTokenHeaders());
    const authorizationToken = (await this.storageService.getItemIonic(AppConstants.AUTH_TOKEN)) || "";
    const refreshToken = (await this.storageService.getItemIonic(AppConstants.x_refresh_token)) || "";
    const timezone = this.timezoneService.timezoneName || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

    return new HttpHeaders({
      authorization: authorizationToken ? `Bearer ${authorizationToken}` : "",
      x_refresh_token: refreshToken,
      acs_token: chatHeaders?.["acs_token"] || "",
      timezone,
    });
  }

  private parseAttachmentUploadResponse(
    response: any,
    fallbackFileName: string
  ): { url: string; fileName: string; message: string } {
    const dataCandidate = response?.data ?? response;
    const normalized = Array.isArray(dataCandidate)
      ? dataCandidate[0]
      : Array.isArray(dataCandidate?.files)
        ? dataCandidate.files[0]
        : dataCandidate;

    return {
      url: String(normalized?.url || "").trim(),
      fileName: String(normalized?.fileName || fallbackFileName).trim(),
      message: String(normalized?.message || response?.message || "").trim(),
    };
  }

  private base64ToBlob(base64Data: string, fallbackMimeType: string): Blob {
    const dataUrlMatch = base64Data.match(/^data:([^;]+);base64,(.+)$/);
    const normalizedBase64 = (dataUrlMatch?.[2] || base64Data).replace(/\s+/g, "");

    // The explicitly resolved mimeType (e.g. the HEIC/HEIF correction applied when the attachment
    // was queued) must win over the data URL's embedded type, which reflects the browser/webview's
    // often-incorrect MIME detection for local file reads and would otherwise silently undo that fix.
    let mimeType = (fallbackMimeType || dataUrlMatch?.[1] || "application/octet-stream") as string;

    // Normalize whitespace, parameters and common non-standard types (server expects image/jpeg)
    mimeType = mimeType.trim().toLowerCase().split(";")[0].trim();
    if (mimeType === "image/jpg") {
      mimeType = "image/jpeg";
    } else if (mimeType === "image/x-png") {
      mimeType = "image/png";
    }

    const binary = atob(normalizedBase64);
    const length = binary.length;
    const bytes = new Uint8Array(length);

    for (let index = 0; index < length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return new Blob([bytes], { type: mimeType });
  }

  async removeQueuedMessage(localId: string): Promise<void> {
    const queue = await this.getQueuedMessages();
    const filtered = queue.filter((item) => item.localId !== localId);
    await this.storageService.setItemIonic(AppConstants.CHAT_OFFLINE_MESSAGE_QUEUE, filtered);
  }

  getValidChatTokens(): Observable<ChatTokens> {
    if (this.isValidTokenPair(this.chatTokens)) {
      return of(this.chatTokens);
    }

    const storedTokens = this.readTokensFromStorage();
    if (this.isValidTokenPair(storedTokens)) {
      this.chatTokens = storedTokens;
      return of(storedTokens);
    }

    return this.generateTokens();
  }

  getValidChatTokenHeaders(): Observable<Record<string, string>> {
    return this.getValidChatTokens().pipe(
      map((tokens) => ({
        acs_token: tokens.acstoken.token,
        bot_token: tokens.botToken.token,
      }))
    );
  }

  clearInMemoryTokens(): void {
    this.chatTokens = null;
    this.generateInFlight$ = null;
  }

  private generateTokens(): Observable<ChatTokens> {
    if (!this.generateInFlight$) {
      this.generateInFlight$ = from(this.httpService.post("chat/generate-acs-token", {})).pipe(
        map((response: GenerateChatTokensResponse) => {
          if (!response?.status || response?.code !== 200 || !response?.data) {
            throw new Error("Failed to generate chat tokens");
          }

          if (!this.isValidTokenPair(response.data)) {
            throw new Error("Invalid chat token pair from API");
          }

          return response.data;
        }),
        tap((tokens) => this.saveTokens(tokens)),
        finalize(() => {
          this.generateInFlight$ = null;
        }),
        shareReplay(1)
      );
    }

    return this.generateInFlight$;
  }

  private saveTokens(tokens: ChatTokens): void {
    this.chatTokens = tokens;
    this.storageService.setItem(AppConstants.CHAT_TOKENS, JSON.stringify(tokens));
  }

  private readTokensFromStorage(): ChatTokens | null {
    const raw = this.storageService.getItem(AppConstants.CHAT_TOKENS);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as ChatTokens;
      return this.isTokenPairShape(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private isValidTokenPair(tokens: ChatTokens | null): tokens is ChatTokens {
    if (!this.isTokenPairShape(tokens)) {
      return false;
    }

    return !this.isExpired(tokens.acstoken.expireTime) && !this.isExpired(tokens.botToken.expireTime);
  }

  private isTokenPairShape(tokens: ChatTokens | null): tokens is ChatTokens {
    return (
      !!tokens?.acstoken?.token &&
      !!tokens?.acstoken?.expireTime &&
      !!tokens?.botToken?.token &&
      !!tokens?.botToken?.expireTime
    );
  }

  private isExpired(expiresOn: string): boolean {
    const time = this.parseTime(expiresOn);
    return !time || time <= Date.now();
  }

  private parseTime(expiresOn: string): number | null {
    const isoTime = new Date(expiresOn).getTime();
    if (!Number.isNaN(isoTime)) {
      return isoTime;
    }

    const match = expiresOn
      .trim()
      .match(/^(\d{2})\.(\d{2})\.(\d{4})\s*(?:\|\s*)?(\d{2}):(\d{2}):(\d{2})$/);

    if (!match) {
      return null;
    }

    const [, dd, mm, yyyy, hh, min, ss] = match;
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(min),
      Number(ss)
    ).getTime();
  }
}